import { useEffect, useMemo, useRef, useState } from "react";
import { getAppPaused, subscribeAppPaused } from "../utils/appPauseStore";
import { FTMS_SAFETY_CONFIG } from "../utils/ftmsSafetyConfig";
import {
  FTMS_FEATURE_CHARACTERISTIC_UUID,
  buildFlatSimulationFrame,
  buildResetFrame,
  parseIndoorBikeSimulationSupported,
} from "../utils/ftmsFrames";
import { isErgActive, normalizeStepMode, shouldAllowFtmsWrite } from "../utils/ergMode";
import { clampGradient } from "../utils/gradientMode";
import { createGradientSender } from "../utils/gradientSender";

// Bluetooth service UUIDs
const FTMS_SERVICE_UUID = 0x1826;
const CYCLING_POWER_SERVICE_UUID = 0x1818;
const CSCS_SERVICE_UUID = 0x1816;
const FITNESS_MACHINE_CONTROL_POINT_UUID = 0x2ad9;
const CYCLING_POWER_MEASUREMENT_UUID = 0x2a63;
const CSC_MEASUREMENT_UUID = 0x2A5B;
// Heart Rate (BPM) UUIDs
const HEART_RATE_SERVICE_UUID = 0x180d;
const HEART_RATE_MEASUREMENT_UUID = 0x2a37;

export const useHomeTrainerBluetooth = (alertDlg, confirm, isMobile, simulate) => {
  // Bluetooth state
  const [serviceFTMS, setServiceFTMS] = useState(null);
  const [serviceCP, setServiceCP] = useState(null);
  const [serviceCSCS, setServiceCSCS] = useState(null);
  const [serviceBPM, setServiceBPM] = useState(null);
  const [canErgControl, setCanErgControl] = useState(false);
  // Rider intent, distinct from the hardware capability above.
  // Deliberately NOT persisted: it resets to true on every connection.
  const [ergEnabled, setErgEnabledState] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isBPMConnected, setIsBPMConnected] = useState(false);
  const [deviceHT, setDeviceHT] = useState(null);
  const [deviceBPM, setDeviceBPM] = useState(null);

  // Sensor data state
  const [currentCadence, setCurrentCadence] = useState(null);
  const [currentPower, setCurrentPower] = useState(null);
  const [currentBPM, setCurrentBPM] = useState(null);
  const [lastUpdatePower, setLastUpdatePower] = useState(Date.now());
  const [lastUpdateCad, setLastUpdateCad] = useState(Date.now());

  // Debug data
  const [currentHTValueCP, setCurrentHTValueCP] = useState(null);
  const [currentHTValueCSCS, setCurrentHTValueCSCS] = useState(null);

  // Variables for cadence calculations
  let previousTimestamp = null;
  let previousCrankRevolutions = null;
  let lastUpdate = null;

  // ---- FTMS Safe Controller (Fitness Machine Control Point 0x2AD9) ----
  // Applies ONLY to FTMS writes. CP/CSCS notifications remain untouched.
  const ftmsConfig = useMemo(
    () => FTMS_SAFETY_CONFIG,
    []
  );

  const pausedRef = useRef(Boolean(getAppPaused()));
  useEffect(() => {
    return subscribeAppPaused((p) => {
      pausedRef.current = Boolean(p);
    });
  }, []);

  const canErgControlRef = useRef(Boolean(canErgControl));
  useEffect(() => {
    canErgControlRef.current = Boolean(canErgControl);
  }, [canErgControl]);

  const ergEnabledRef = useRef(true);
  // Kept as a safety net: it covers any state change that does not come through
  // the wrapper below (a future direct setErgEnabledState, StrictMode remounts).
  useEffect(() => {
    ergEnabledRef.current = Boolean(ergEnabled);
  }, [ergEnabled]);

  // The exported setter. The effect above only catches up on the NEXT render,
  // so a caller doing setErgEnabled(true) and then, in the same handler,
  // engageErg() + setTargetPowerViaBt(...) would still read
  // ergEnabledRef.current === false, compute active === false and release the
  // ERG the rider just asked for. Writing the ref synchronously first removes
  // the skew for every caller. Supports the functional form too, reading the
  // ref (already up to date) rather than the not-yet-committed state.
  const setErgEnabled = (value) => {
    const next =
      typeof value === "function"
        ? Boolean(value(ergEnabledRef.current))
        : Boolean(value);
    ergEnabledRef.current = next;
    setErgEnabledState(next);
  };

  // Set once per connection from the Fitness Machine Feature characteristic.
  const canSimulateFlatRef = useRef(false);
  // Rendering mirror of the ref above. A trainer that rejects Indoor Bike
  // Simulation has no gradient at all, so the UI must hide the control rather
  // than offer a setting that cannot reach the trainer.
  const [canSimulateFlat, setCanSimulateFlat] = useState(false);
  // Guards release idempotence: we send the release frame once per transition,
  // not on every tick of the session loop.
  const ergReleasedRef = useRef(false);
  // The gradient currently applied to the trainer, in percent. Kept so a
  // re-release repeats the gradient the rider had set rather than dropping
  // back to flat.
  const currentGradientRef = useRef(0);
  // Declared here rather than beside its creation below so nothing in this file
  // can reference it before the binding exists.
  const gradientSenderRef = useRef(null);
  // Re-entrancy guard: the flag above is only set after up to rateLimitMs of
  // awaits, while the session loop ticks about every second. Concurrent callers
  // share this in-flight promise instead of each starting their own release.
  const ergReleaseInFlightRef = useRef(null);

  const simulateRef = useRef(Boolean(simulate));
  useEffect(() => {
    simulateRef.current = Boolean(simulate);
  }, [simulate]);

  const serviceFTMSRef = useRef(null);
  useEffect(() => {
    serviceFTMSRef.current = serviceFTMS;
  }, [serviceFTMS]);

  const ftmsControlPointRef = useRef(null);
  const ftmsControlGrantedRef = useRef(false);

  const ftmsStateRef = useRef({
    // Timing
    lastWriteAtMs: 0,

    // Desired / last-sent target for debounce + anti-jitter
    desiredTargetW: null,
    lastSentTargetW: null,

    // Timers
    sendTimerId: null,
    resumeTimerId: null,

    // Lifecycle block
    blocked: true, // stays blocked until Request Control succeeds after a connect
    requestingControl: false,
  });

  const ftmsIsDocumentVisible = () => {
    try {
      return !document.hidden && document.visibilityState === "visible";
    } catch {
      return true;
    }
  };

  const ftmsClearTimers = () => {
    const st = ftmsStateRef.current;
    if (st.sendTimerId) {
      clearTimeout(st.sendTimerId);
      st.sendTimerId = null;
    }
    if (st.resumeTimerId) {
      clearTimeout(st.resumeTimerId);
      st.resumeTimerId = null;
    }
  };

  const ftmsReset = ({ block } = { block: true }) => {
    ftmsClearTimers();
    const st = ftmsStateRef.current;
    st.desiredTargetW = null;
    st.requestingControl = false;
    ftmsControlPointRef.current = null;
    ftmsControlGrantedRef.current = false;
    // Per-connection capability, read from the Fitness Machine Feature
    // characteristic on connect: cleared here for symmetry with the refs above.
    canSimulateFlatRef.current = false;
    setCanSimulateFlat(false);
    // A queued gradient frame must not survive a disconnect and land on the
    // next trainer.
    if (gradientSenderRef.current) gradientSenderRef.current.cancel();
    currentGradientRef.current = 0;
    st.blocked = Boolean(block);
  };

  const ftmsEnsureControlPoint = async () => {
    const service = serviceFTMSRef.current;
    if (!service) return null;
    if (ftmsControlPointRef.current) return ftmsControlPointRef.current;
    try {
      const cp = await service.getCharacteristic(FITNESS_MACHINE_CONTROL_POINT_UUID);
      ftmsControlPointRef.current = cp;
      return cp;
    } catch (e) {
      console.error("FTMS: failed to get control point characteristic", e);
      return null;
    }
  };

  const ftmsWriteRaw = async (buffer, { reason, allowWhilePaused = false } = {}) => {
    // All FTMS writes MUST flow through here.
    // In simulate mode we still run the exact same scheduling/rate-limit path
    // below; only the actual bluetooth write is skipped ("send" log kept).
    if (
      !shouldAllowFtmsWrite({
        visible: ftmsIsDocumentVisible(),
        paused: pausedRef.current,
        blocked: ftmsStateRef.current.blocked,
        simulate: simulateRef.current,
        canErgControl: canErgControlRef.current,
        hasService: Boolean(serviceFTMSRef.current),
        allowWhilePaused,
      })
    ) {
      return false;
    }

    // Rate-limit applies to all FTMS commands, including Request Control.
    const now = Date.now();
    const st = ftmsStateRef.current;
    const msSince = now - (st.lastWriteAtMs || 0);
    if (msSince < ftmsConfig.rateLimitMs) {
      const delay = ftmsConfig.rateLimitMs - msSince;
      // Re-schedule the existing send path; callers should use higher-level APIs.
      if (!st.sendTimerId) {
        st.sendTimerId = setTimeout(() => {
          st.sendTimerId = null;
          // Flush will send the latest desired target only (debounce rule).
          ftmsFlush({ reason: "rate-limit-timer" });
        }, delay);
      }
      return false;
    }

    // Simulated "send": do not touch WebBluetooth, but behave as if it was written now.
    if (simulateRef.current) {
      st.lastWriteAtMs = Date.now();
      const bytes = Array.from(new Uint8Array(buffer));
      const op = bytes?.length ? `0x${bytes[0].toString(16).padStart(2, "0")}` : "n/a";
      const maybeTargetW =
        bytes?.length >= 3 && bytes[0] === 0x05
          ? new DataView(buffer).getInt16(1, true)
          : null;
      console.log(
        `[FTMS SIM SEND] op=${op}` +
          (maybeTargetW != null ? ` targetW=${maybeTargetW}` : "") +
          (reason ? ` reason=${reason}` : "") +
          ` at=${new Date().toISOString()}`
      );
      return true;
    }

    const cp = await ftmsEnsureControlPoint();
    if (!cp) return false;

    try {
      await cp.writeValue(buffer);
      st.lastWriteAtMs = Date.now();
      // Log ONLY at the exact moment we actually wrote to the trainer.
      const bytes = Array.from(new Uint8Array(buffer));
      const op = bytes?.length ? `0x${bytes[0].toString(16).padStart(2, "0")}` : "n/a";
      const maybeTargetW =
        bytes?.length >= 3 && bytes[0] === 0x05
          ? new DataView(buffer).getInt16(1, true)
          : null;
      console.log(
        `[FTMS SEND] op=${op}` +
          (maybeTargetW != null ? ` targetW=${maybeTargetW}` : "") +
          (reason ? ` reason=${reason}` : "") +
          ` at=${new Date().toISOString()}`
      );
      return true;
    } catch (e) {
      console.error("FTMS: write failed", e);
      // Conservative: block further writes until we successfully re-request control.
      st.blocked = true;
      ftmsControlGrantedRef.current = false;
      return false;
    }
  };

  const ftmsRequestControl = async ({ allowWhilePaused = false } = {}) => {
    const st = ftmsStateRef.current;
    if (st.requestingControl) return;
    if (!ftmsIsDocumentVisible()) return;
    if (pausedRef.current && !allowWhilePaused) return;
    if (!simulateRef.current) {
      if (!canErgControlRef.current) return;
      if (!serviceFTMSRef.current) return;
    }

    st.requestingControl = true;
    try {
      const buffer = new ArrayBuffer(1);
      const dv = new DataView(buffer);
      dv.setUint8(0, 0x00); // OpCode: Request Control
      // Temporarily allow this even if blocked, since this is how we unblock.
      const prevBlocked = st.blocked;
      st.blocked = false;
      const ok = await ftmsWriteRaw(buffer, { reason: "request-control", allowWhilePaused });
      if (ok) {
        ftmsControlGrantedRef.current = true;
        st.blocked = false;
      } else {
        st.blocked = true;
        ftmsControlGrantedRef.current = false;
      }
      // If we were previously blocked, keep it blocked unless we succeeded.
      if (prevBlocked && !ok) {
        st.blocked = true;
      }
    } finally {
      st.requestingControl = false;
    }
  };

  const ftmsClampTargetW = (w) => {
    const n = Number(w);
    if (!Number.isFinite(n)) return null;
    const rounded = Math.round(n);
    return Math.max(ftmsConfig.minW, Math.min(ftmsConfig.maxW, rounded));
  };

  const ftmsScheduleFlush = ({ reason } = {}) => {
    const st = ftmsStateRef.current;
    if (st.sendTimerId) return; // Debounce: keep only latest desired value

    const now = Date.now();
    const msSince = now - (st.lastWriteAtMs || 0);
    const delay = Math.max(0, ftmsConfig.rateLimitMs - msSince);

    // If we're allowed to send now, flush ASAP; otherwise schedule for the first allowed moment.
    st.sendTimerId = setTimeout(() => {
      st.sendTimerId = null;
      ftmsFlush({ reason: reason || "scheduled-flush" });
    }, delay);
  };

  const ftmsFlush = async ({ reason } = {}) => {
    const st = ftmsStateRef.current;

    if (!ftmsIsDocumentVisible()) return;
    if (pausedRef.current) return;
    if (!simulateRef.current) {
      if (!canErgControlRef.current) return;
      if (!serviceFTMSRef.current) return;
    }

    // If control isn't granted yet (or we previously blocked), request it first.
    if (!ftmsControlGrantedRef.current || st.blocked) {
      await ftmsRequestControl();
      // If still no control, stop here (and keep desiredTargetW for later).
      if (!ftmsControlGrantedRef.current || st.blocked) return;
    }

    const targetW = st.desiredTargetW;
    if (targetW == null) return;

    // Rate-limit guard (in case we were called directly)
    const now = Date.now();
    const msSince = now - (st.lastWriteAtMs || 0);
    if (msSince < ftmsConfig.rateLimitMs) {
      ftmsScheduleFlush({ reason: "rate-limit-guard" });
      return;
    }

    const buffer = new ArrayBuffer(3);
    const dv = new DataView(buffer);
    dv.setUint8(0, 0x05); // OpCode: Set Target Power
    dv.setInt16(1, parseInt(targetW, 10), true);

    const ok = await ftmsWriteRaw(buffer, { reason: reason || "set-target-power" });
    if (ok) {
      st.lastSentTargetW = targetW;
    }
  };

  const ftmsSetDesiredTarget = (watts) => {
    const target = ftmsClampTargetW(watts);
    if (target == null) return;

    const st = ftmsStateRef.current;
    const reference = st.desiredTargetW ?? st.lastSentTargetW;
    if (reference != null && Math.abs(target - reference) < ftmsConfig.antiJitterW) {
      return; // Anti-jitter: ignore tiny changes
    }

    st.desiredTargetW = target; // Debounce: always keep latest desired target

    if (!ftmsIsDocumentVisible()) {
      // While hidden: never schedule, never write. Desired target is kept for resume.
      ftmsClearTimers();
      return;
    }

    if (pausedRef.current) {
      // While paused: never schedule, never write. Desired target is kept for resume.
      ftmsClearTimers();
      return;
    }

    if (!simulateRef.current) {
      if (!canErgControlRef.current || !serviceFTMSRef.current) return;
    }

    // If we are blocked (disconnected / no control), don't schedule yet.
    if (st.blocked || !ftmsControlGrantedRef.current) return;

    ftmsScheduleFlush({ reason: "desired-target-updated" });
  };

  // Hand resistance back to the rider. Idempotent per transition.
  //
  // Deliberately does not go through ftmsFlush: that path re-sends the latest
  // desired POWER target, which is the opposite of what a release means.
  const performReleaseErg = async (reason, gradePercent = 0) => {
    // Drop any queued target so a pending flush cannot re-engage behind our back.
    const st = ftmsStateRef.current;
    ftmsClearTimers();
    st.desiredTargetW = null;

    // A release needs control like any other write; re-acquire it if we lost it.
    if (!ftmsControlGrantedRef.current || st.blocked) {
      await ftmsRequestControl({ allowWhilePaused: true });
      if (!ftmsControlGrantedRef.current || st.blocked) return false;
    }

    // Simulation never reads the feature characteristic — that only happens on a
    // real connect — so treat it as flat-capable, otherwise every simulated
    // release would emit a bare Reset instead of the expected op=0x11.
    // Local name deliberately differs from the `canSimulateFlat` state above:
    // this is the capability as the radio sees it right now, simulation
    // included, not the rendering mirror.
    const simCapable = canSimulateFlatRef.current || simulateRef.current;
    const grade = clampGradient(gradePercent);
    const buffer = simCapable
      ? buildFlatSimulationFrame({ gradePercent: grade })
      : buildResetFrame();
    if (simCapable) currentGradientRef.current = grade;
    const ok = await ftmsWriteRaw(buffer, { reason, allowWhilePaused: true });

    if (!ok) {
      // A throwing cp.writeValue sets blocked and drops the control grant, and
      // `blocked` is deliberately never exempted in shouldAllowFtmsWrite — so
      // without re-running the acquisition block the retry would refuse itself
      // without ever touching the radio. Re-acquire first and wait out the rate
      // limit afterwards, so the retry is not refused for writing too soon.
      if (!ftmsControlGrantedRef.current || st.blocked) {
        await ftmsRequestControl({ allowWhilePaused: true });
        if (!ftmsControlGrantedRef.current || st.blocked) {
          console.warn("FTMS: release failed; trainer may still be holding the last target");
          return false;
        }
      }
      // Retry once past the rate limit, as a release — not as a power target.
      await new Promise((resolve) => setTimeout(resolve, ftmsConfig.rateLimitMs));
      const retried = await ftmsWriteRaw(buffer, {
        reason: `${reason}-retry`,
        allowWhilePaused: true,
      });
      if (!retried) {
        console.warn("FTMS: release failed; trainer may still be holding the last target");
        return false;
      }
    }

    ergReleasedRef.current = true;
    st.lastSentTargetW = null;

    // Repeat the clearing done at the top of this function. The flag above is
    // only set HERE, after up to rateLimitMs of awaits, so an active tick
    // landing inside that window still sees ergReleasedRef false, skips the
    // engage block in setTargetPowerViaBt and reaches ftmsSetDesiredTarget --
    // which stores a target and, while the grant and !blocked still hold,
    // schedules a flush. That timer would outlive the release and push a 0x05
    // (ftmsFlush re-requests control when blocked), silently undoing it; and
    // the surviving desiredTargetW would become the anti-jitter reference, so
    // the next re-engage at the same wattage would return early and run at
    // flat-road resistance. The release protects its frame against
    // concurrency; this protects the state that frame depends on.
    ftmsClearTimers();
    st.desiredTargetW = null;

    // Reset (0x01) drops control at the trainer, so mirror that locally and let
    // the next engage re-request it.
    //
    // After 0x11 we now KEEP control, which is a change from the first version
    // of this function. The rider can adjust the simulated gradient at any
    // moment while ERG is off, and re-acquiring for every ±1% would cost an
    // extra Request Control plus a whole rate-limit window before the change is
    // felt under the pedals.
    //
    // Safety note, because this looks like it loosens something: dropping
    // control was never what stopped a stray flush from pushing a 0x05 here.
    // `st.desiredTargetW = null` above is. ftmsFlush returns early on a null
    // target regardless of the grant, so retaining control opens no power-write
    // path. What dropping control did do was force the next engage through
    // ftmsRequestControl; engageErg still calls it unconditionally, so that
    // path is unchanged.
    if (simCapable) {
      st.blocked = false;
    } else {
      // NOTE: on this branch `blocked` stays overloaded — it means both
      // "control was lost or a write failed" and "we deliberately released",
      // and nothing tells the two apart. Benign only because desiredTargetW was
      // cleared above. engageErg is the single way back out of either meaning.
      ftmsControlGrantedRef.current = false;
      st.blocked = true;
    }
    return true;
  };

  // Public entry point: idempotent per transition, and re-entrant. The flag is
  // only set after up to rateLimitMs of awaits while the session loop ticks
  // about every second, so concurrent callers share one in-flight release
  // instead of sending a second frame — and instead of the losing caller
  // reporting a false failure once the winner has set blocked.
  const releaseErg = (reason = "erg-release", gradePercent = 0) => {
    if (ergReleasedRef.current) return Promise.resolve(true);
    if (ergReleaseInFlightRef.current) return ergReleaseInFlightRef.current;

    const inFlight = performReleaseErg(reason, gradePercent)
      .catch((e) => {
        // Nothing in the body throws today, but setTargetPowerViaBt awaits this
        // bare and is itself called un-awaited from the session loop, so a
        // rejection would surface as an unhandled rejection instead of a
        // failed release. Report it the way a failed release reports.
        console.warn(
          "FTMS: release threw; trainer may still be holding the last target",
          e
        );
        return false;
      })
      .finally(() => {
        ergReleaseInFlightRef.current = null;
      });
    ergReleaseInFlightRef.current = inFlight;
    return inFlight;
  };

  // Take the trainer back under control. The caller re-sends the target after.
  //
  // Request Control carries the paused exemption on purpose: it is not a power
  // command — it changes no resistance, it only tells the trainer the app is
  // driving again. The ERG toggle is usable only while paused, so without the
  // exemption this, the only re-enable path, could never succeed. The guards
  // that actually protect the rider stay unexempted: ftmsSetDesiredTarget and
  // ftmsFlush still refuse to send a power target while paused.
  const engageErg = async () => {
    const st = ftmsStateRef.current;
    // Drop any queued gradient frame: the trainer is going back under power
    // control, and a late 0x11 landing after the first 0x05 would silently put
    // it back into simulation.
    if (gradientSenderRef.current) gradientSenderRef.current.cancel();
    await ftmsRequestControl({ allowWhilePaused: true });
    const ok = ftmsControlGrantedRef.current && !st.blocked;
    // Clear the released flag only once control is actually back, so a failed
    // engage is retried by the next active tick rather than falling through to
    // a write path that `blocked` would swallow anyway.
    if (ok) ergReleasedRef.current = false;
    return ok;
  };

  // Debounced simulated-gradient writer.
  //
  // The sender owns the timing (latest-value-wins, one frame per rate-limit
  // window) and is unit-tested in utils/gradientSender.js. This closure owns the
  // guards. A gradient frame is a simulation parameter, not a power target, so
  // it carries the paused exemption for the same reason the release does — and
  // it still obeys the visibility guard inside ftmsWriteRaw, like every write.
  if (gradientSenderRef.current === null) {
    gradientSenderRef.current = createGradientSender({
      minIntervalMs: ftmsConfig.rateLimitMs,
      write: async (grade) => {
        if (!canSimulateFlatRef.current && !simulateRef.current) return;
        const st = ftmsStateRef.current;
        if (!ftmsControlGrantedRef.current || st.blocked) {
          await ftmsRequestControl({ allowWhilePaused: true });
          if (!ftmsControlGrantedRef.current || st.blocked) return;
        }
        const buffer = buildFlatSimulationFrame({ gradePercent: grade });
        const ok = await ftmsWriteRaw(buffer, {
          reason: `gradient:${grade}`,
          allowWhilePaused: true,
        });
        if (ok) currentGradientRef.current = grade;
      },
    });
  }

  // A trainer that refused Indoor Bike Simulation has no gradient at all, so
  // this is a no-op there rather than a silent failure deeper down.
  const setSimulationGradient = (percent) => {
    if (!canSimulateFlatRef.current && !simulateRef.current) return;
    const next = clampGradient(percent);
    // Skip a frame that would change nothing. Without this, cutting ERG sends
    // two identical 0x11 in a row -- one from the release, which already
    // carries the mode's gradient, and one from the caller's own follow-up --
    // burning a rate-limit window so the rider's next tap lands late.
    // Only safe to skip when nothing is queued: a pending value means the
    // trainer is not on currentGradientRef yet.
    if (
      next === currentGradientRef.current &&
      gradientSenderRef.current.pending() === null
    ) {
      return;
    }
    gradientSenderRef.current.send(next);
  };

  // Visibility + focus listeners (registered exactly once)
  const visibilityListenersRegisteredRef = useRef(false);
  useEffect(() => {
    // Intentionally register listeners exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (visibilityListenersRegisteredRef.current) return;
    visibilityListenersRegisteredRef.current = true;

    const onVisibilityChange = () => {
      if (!ftmsIsDocumentVisible()) {
        // Background behavior: cancel timers; clear scheduled writes; do not send while hidden.
        ftmsClearTimers();
        // Do NOT write while hidden (handled by guards)
        return;
      }
      // Resume behavior: wait cooldown then send only latest desired target (if any)
      const st = ftmsStateRef.current;
      ftmsClearTimers();
      st.resumeTimerId = setTimeout(() => {
        st.resumeTimerId = null;
        ftmsFlush({ reason: "resume-visible" });
      }, ftmsConfig.resumeCooldownMs);
    };

    // Focus can matter on some browsers; treat similarly to visibility.
    const onFocus = () => {
      if (!ftmsIsDocumentVisible()) return;
      const st = ftmsStateRef.current;
      if (st.resumeTimerId) return;
      st.resumeTimerId = setTimeout(() => {
        st.resumeTimerId = null;
        ftmsFlush({ reason: "resume-focus" });
      }, ftmsConfig.resumeCooldownMs);
    };

    const onBlur = () => {
      // Be conservative: cancel timers while blurred. (Still won't write while hidden anyway.)
      ftmsClearTimers();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
      visibilityListenersRegisteredRef.current = false;
      ftmsReset({ block: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ftmsConfig.resumeCooldownMs]);

  // Note: pause transitions are handled via the store + runtime guards.

  const handleCharacteristicValueChangedBPM = (event) => {
    const value = event.target.value;
    const heartRate = value.getUint8(1); // Heart Rate BPM
    console.log(`Heart Rate: ${heartRate} BPM`);
    setCurrentBPM(heartRate);
  };

  // If FTMS service disappears (disconnect/reset), block + cancel pending writes.
  useEffect(() => {
    if (!serviceFTMS) {
      ftmsReset({ block: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceFTMS]);

  const handleCharacteristicValueChangedCP = (event, withCadence) => {
    const value = event.target.value;
    // Convert DataView to an array of bytes for storage
    const buffer = new ArrayBuffer(value.byteLength);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < value.byteLength; i++) {
      view[i] = value.getUint8(i);
    }
    setCurrentHTValueCP(view); //store for debug

    const flags = value.getUint16(0, true); // Read flags field
    // Instantaneous Power field (always present, INT16)
    const instantaneousPower = value.getInt16(2, true);
    setLastUpdatePower(Date.now());
    console.log(`Instantaneous Power: ${instantaneousPower} Watts`);
    setCurrentPower(instantaneousPower);

    // Check for other optional data based on flags
    // Example: Check if Crank Revolution Data is present (bit 5)
    if (withCadence && flags & (1 << 5)) {
      console.log("using CP cadence")
      // Bitwise to check if the 6th bit is set
      let crankRevolutions, lastCrankEventTime;
      if (value.byteLength === 14) {
        // Use offsets for a 14-byte array
        crankRevolutions = value.getUint16(10, true);
        lastCrankEventTime = value.getUint16(12, true);
      } else if (value.byteLength === 16) {
        // Use offsets for a 16-byte array
        crankRevolutions = value.getUint16(12, true);
        lastCrankEventTime = value.getUint16(14, true);
      } else {
        crankRevolutions = value.getUint16(10, true); //testing that, Suito initial
        lastCrankEventTime = value.getUint16(12, true);
        console.error("Unsupported value length:", value.byteLength);
      }

      if (previousCrankRevolutions != null && previousTimestamp != null) {
        const timeDifference = (lastCrankEventTime - previousTimestamp) / 1024; // Convert to seconds
        const revolutionsDifference =
          crankRevolutions - previousCrankRevolutions;

        if (revolutionsDifference > 0 || Date.now() - lastUpdate >= 3000) {
          // Basically if not even one rotation in 3 sec, RMP drop to zero.
          const cadence = (revolutionsDifference / timeDifference) * 60; // RPM
          console.log(`Cadence CP: ${cadence.toFixed(2)} RPM`);
          setCurrentCadence(Math.round(cadence) >= 0 ? Math.round(cadence) : 0);
          lastUpdate = Date.now()
          setLastUpdateCad(lastUpdate);
          previousTimestamp = lastCrankEventTime;
          previousCrankRevolutions = crankRevolutions;
        }
      } else {
        previousTimestamp = lastCrankEventTime;
        previousCrankRevolutions = crankRevolutions;
      }
    }

    // You can add similar checks and calculations for other data based on other bits
  };

  const handleCharacteristicValueChangedCSCS = (event) => {
    const value = event.target.value;

    if (!value || value.byteLength === 0) {
      console.warn("Received empty characteristic data.");
      return;
    }

    // Convert DataView to an array of bytes for debugging
    const rawData = new Uint8Array(value.buffer);
    setCurrentHTValueCSCS(rawData); // Store raw data

    const flags = value.getUint8(0);
    let index = 1;

    const wheelRevolutionDataPresent = flags & 0x01;
    const crankRevolutionDataPresent = flags & 0x02;

    let cadence = null; // Default to null to track valid updates

    if (crankRevolutionDataPresent) {


      // Skip Wheel Revolution Data if present
      if (wheelRevolutionDataPresent) {

        index += 6;
      }

      // Extract crank revolutions (Little-endian)
      console.log("index crank rev", index)
      const crankRevolutions = value.getUint16(index, true);
      index += 2;
      console.log("index crank time rev", index)

      // Extract crank event time (Little-endian, in 1/1024 sec)
      const crankEventTime = value.getUint16(index, true);

      console.log(`Crank Revolutions: ${crankRevolutions}, Crank Event Time: ${crankEventTime}`);
      console.log(`Previous Timestamp: ${previousTimestamp}, Current Timestamp: ${crankEventTime}`);

      if (previousCrankRevolutions !== null && previousTimestamp !== null) {
        let deltaRevolutions = crankRevolutions - previousCrankRevolutions;
        let deltaTime;

        // ⚠️ Correction du compteur qui reset
        if (crankEventTime < previousTimestamp) {
          console.warn("⚠️ Compteur `crank_event_time` réinitialisé !");
          deltaTime = ((crankEventTime + 65536) - previousTimestamp) / 1024.0;
        } else {
          deltaTime = (crankEventTime - previousTimestamp) / 1024.0;
        }

        console.log(`Raw Delta Time (before scaling): ${crankEventTime - previousTimestamp}`);
        console.log(`Computed Delta Time (seconds): ${deltaTime}`);

        // 🚨 Éviter les deltas trop petits
        if (deltaTime < 0.3) {
          console.warn("⚠️ deltaTime trop petit, on l'ignore pour éviter un pic.");
          return;
        }

        console.log(`Delta Revolutions: ${deltaRevolutions}, Delta Time: ${deltaTime.toFixed(4)} sec`);

        if (deltaTime > 0 && (deltaRevolutions > 0 || Date.now() - lastUpdate >= 3000)) {
          cadence = (deltaRevolutions / deltaTime) * 60;

          // 🔥 Appliquer un **lissage** pour éviter les pics
          const ALPHA = 0; // Facteur de lissage // pas vraiment besoin car déjà lissé au sliding
          cadence = currentCadence ? (cadence * ALPHA + currentCadence * (1 - ALPHA)) : cadence;

          console.log(`Cadence CSCS: ${cadence.toFixed(2)} RPM, last update : ${Date.now() - lastUpdate}`);
          setCurrentCadence(Math.max(0, Math.round(cadence)));
          lastUpdate = Date.now()
          setLastUpdateCad(lastUpdate);

          // Update previous values only when cadence is computed
          previousCrankRevolutions = crankRevolutions;
          previousTimestamp = crankEventTime;
        }
      } else {
        // First data received, just initialize
        previousCrankRevolutions = crankRevolutions;
        previousTimestamp = crankEventTime;
      }
      if (Date.now() - lastUpdate >= 3000) {
        console.warn("🚨 Aucune mise à jour de cadence depuis 3s, reset à 0 !");
        setCurrentCadence(0);
        lastUpdate = Date.now()
        setLastUpdateCad(lastUpdate);
      }
    }


  };

  const subscribeToMetricCP = async (service, withCadence) => {
    // Get the Cycling Power Measurement characteristic
    const characteristic = await service.getCharacteristic(CYCLING_POWER_MEASUREMENT_UUID);

    console.log("Starting notifications CP...");

    // Start listening for updates from the characteristic
    await characteristic.startNotifications();

    // Add the event listener for when the characteristic value changes
    characteristic.addEventListener(
      "characteristicvaluechanged",
      (event) => handleCharacteristicValueChangedCP(event, withCadence)
    );
  };

  const subscribeToMetricCSCS = async (service) => {
    const characteristic = await service.getCharacteristic(CSC_MEASUREMENT_UUID);

    console.log("Starting notifications CSCS...");
    await characteristic.startNotifications();

    characteristic.addEventListener(
      "characteristicvaluechanged",
      handleCharacteristicValueChangedCSCS)
  };

  const handleIndications = (event) => {
    const value = event.target.value; // process your incoming data
    console.log("Received indication:", value);
  };

  const setTargetPowerViaBt = async (service, watts, stepMode = "erg") => {
    const mode = normalizeStepMode(stepMode);
    const active = isErgActive({
      canErgControl: canErgControlRef.current || simulateRef.current,
      ergEnabled: ergEnabledRef.current,
      stepMode: mode,
    });

    if (!active) {
      // A trainer that never had ERG control has nothing to release: keep the
      // pre-existing manual-mode log instead of running a release that can only
      // clear timers and then fail.
      if (!canErgControlRef.current && !simulateRef.current) {
        console.log(
          `ERG control not available (manual mode). Target was ${Math.round(watts)}W; user must adjust resistance manually.`
        );
        return;
      }
      await releaseErg(`inactive:${mode}`);
      return;
    }

    // Coming back from a release: releaseErg deliberately leaves `blocked` set
    // and the control grant cleared, so merely clearing the flag here would let
    // ftmsSetDesiredTarget bail out on this tick and anti-jitter swallow the
    // next one. Re-acquire control instead, so released -> active heals itself
    // without depending on an external caller.
    if (ergReleasedRef.current) {
      if (simulateRef.current) {
        // The simulation branch below already re-requests control on every tick;
        // a second request inside one rate-limit window would be refused and
        // would re-block us, so here we only drop the flag.
        ergReleasedRef.current = false;
      } else {
        const engaged = await engageErg();
        // Still released: leave the flag set so the next active tick retries,
        // and leave desiredTargetW null so anti-jitter cannot swallow the
        // eventual re-send.
        if (!engaged) return;
      }
    }

    // In simulation mode: run the exact same FTMS control pipeline, but skip the actual bluetooth write.
    if (simulateRef.current) {
      // Ensure internal refs satisfy guards so we can simulate Request Control + Set Target Power.
      // We intentionally keep safety guards for pause/visibility.
      canErgControlRef.current = true;
      if (!serviceFTMSRef.current) {
        // If no FTMS service exists (simulation), provide a placeholder so request-control can proceed.
        serviceFTMSRef.current = service || { __simulated_ftms: true };
      }
      // Make sure we go through the same "request control then write" lifecycle as a real device.
      // This will produce a [FTMS SIM SEND] log for Request Control (op=0x00) and later for Set Target Power (op=0x05).
      await ftmsRequestControl();
      // Debounced, clamped, rate-limited FTMS write (will log at the actual "send" moment).
      ftmsSetDesiredTarget(watts);
      return;
    }

    if (!canErgControl || !service) {
      console.log(
        `ERG control not available (manual mode). Target was ${Math.round(watts)}W; user must adjust resistance manually.`
      );
      return;
    }
    if (!simulate && service) {
      // Ensure controller sees the latest FTMS service (some call-sites pass it explicitly).
      serviceFTMSRef.current = service;
      // Debounced, clamped, rate-limited FTMS write (never sends while hidden/paused).
      ftmsSetDesiredTarget(watts);
      return;
    } else {
      console.log(
        `Target power simulation set to ${watts} watts to control`
      );
    }
  };

  const connectToBPM = async () => {
    let device;
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (isMobile && /iPad|iPhone|iPod/i.test(userAgent) && !window.MSStream) {
      alertDlg(
        "Oops! It looks like iOS devices (iPhone, iPad) don't support Bluetooth on web apps. Unfortunately, this means you won't be able to connect your home trainer. But don't worry! You can still use your computer, including MacBooks, for a seamless experience. Quick reminder: while MacOS > Windows, we all know Android > iOS ;)",
        {
          title: "iOS Bluetooth limitation",
          reportBug: {
            alertName: "HomeTrainerBluetooth: iOS detected (Bluetooth not supported on web apps)",
            fields: ["Device type (iPhone / iPad / Other)"],
            placeholder: "Please confirm:\n- iPhone\n- iPad\n- Other (which?)\n\nOptional: iOS version + browser (ex: iOS 18.2, Safari)",
            sendText: "Send info",
          },
        }
      );
      return;
    }
    else if (!navigator.bluetooth) {
      console.error("Web Bluetooth API is not available in this browser.");
      alertDlg(
        "Web Bluetooth API is not available in this browser. Please use Chrome or another supported browser.",
        {
          title: "Bluetooth not supported on heart sensor",
          reportBug: {
            alertName: `${isMobile ? "Mobile Mode" : "Desktop Mode"} Web Bluetooth API not available for BPM`,
            fields: ["Browser type + version (ex: Chrome 131, Edge 131, Safari 18)", "OS (Windows / macOS / Android / iOS)"],
            placeholder: "Example:\nChrome 131 on Windows 11\n\nAnything else helpful (optional):",
            sendText: "Send info",
          },
        }
      );
      return;
    }

    try {
      console.log("User initiated connection for heart rate service...");
      device = await navigator.bluetooth.requestDevice({
        filters: [
          { services: [HEART_RATE_SERVICE_UUID] }, // Heart Rate service
        ],
        optionalServices: [
          HEART_RATE_SERVICE_UUID, // Heart Rate service
        ],
      });

      console.log(`Connecting to ${device.name}`);
      const server = await device.gatt.connect();
      console.log("BPM device connected");

      const service = await server.getPrimaryService(HEART_RATE_SERVICE_UUID);
      console.log("Heart Rate service", service);

      setDeviceBPM(device);
      setServiceBPM(service);

      const heartRateMeasurement = await service.getCharacteristic(HEART_RATE_MEASUREMENT_UUID);
      console.log("Heart Rate Measurement characteristic", heartRateMeasurement);

      // Subscribe to heart rate notifications
      await heartRateMeasurement.startNotifications();
      heartRateMeasurement.addEventListener(
        "characteristicvaluechanged",
        handleCharacteristicValueChangedBPM
      );

      console.log("Subscribed to heart rate notifications");
      setIsBPMConnected(true);
    } catch (error) {
      console.error("Failed to connect BPM:", error);
      setIsBPMConnected(false);
      try {
        if (device?.gatt?.connected) {
          device.gatt.disconnect();
        }
      } catch (e) {
        console.log("Failed to disconnect after error when connecting BPM:", e);
      }
    }
  };

  const disconnectFromBPM = async () => {
    if (!(await confirm("Disconnect heart rate sensor ?"))) {
      return;
    }
    try {
      if (deviceBPM && deviceBPM.gatt && deviceBPM.gatt.connected && serviceBPM) {
        console.log(`Disconnecting from ${deviceBPM.name}...`);

        // Stop notifications and remove event listener
        const heartRateMeasurement = await serviceBPM.getCharacteristic(HEART_RATE_MEASUREMENT_UUID);
        await heartRateMeasurement.stopNotifications();
        heartRateMeasurement.removeEventListener(
          "characteristicvaluechanged",
          handleCharacteristicValueChangedBPM
        );

        deviceBPM.gatt.disconnect();

        console.log("BPM device disconnected");
        // Reset device and service
        setDeviceBPM(null);
        setServiceBPM(null);
        setIsBPMConnected(false);
        setCurrentBPM(null);
      } else {
        console.log("No BPM device is connected.");
      }
    } catch (error) {
      console.error("Failed to disconnect BPM:", error);
    }
  };

  const connectToDevice = async (onConnect, onDisconnect, options = {}) => {

    const useCadenceService = "CSCS" //or CP
    let device;
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const requestedMode = options?.mode === "sensorsOnly" ? "sensorsOnly" : "ftms";

    if (isMobile && /iPad|iPhone|iPod/i.test(userAgent) && !window.MSStream) {
      alertDlg(
        "Oops! It looks like iOS devices (iPhone, iPad) don't support Bluetooth on web apps. Unfortunately, this means you won't be able to connect your home trainer. But don't worry! You can still use your computer, including MacBooks, for a seamless experience. Quick reminder: while MacOS > Windows, we all know Android > iOS ;)",
        {
          title: "iOS Bluetooth limitation",
          reportBug: {
            alertName: "HomeTrainerBluetooth: iOS detected (Bluetooth not supported on web apps)",
            fields: ["Device type (iPhone / iPad / Other)"],
            placeholder: "Please confirm:\n- iPhone\n- iPad\n- Other (which?)\n\nOptional: iOS version + browser (ex: iOS 18.2, Safari)",
            sendText: "Send info",
          },
        }
      );
      return;
    }
    else if (!navigator.bluetooth) {
      console.error("Web Bluetooth API is not available in this browser.");
      alertDlg(
        "Web Bluetooth API is not available in this browser. Please use Chrome or another supported browser.",
        {
          title: "Bluetooth not supported on device",
          reportBug: {
            alertName: `${isMobile ? "Mobile Mode" : "Desktop Mode"} Web Bluetooth API not available for device`,
            fields: ["Browser type + version (ex: Chrome 131, Edge 131, Safari 18)", "OS (Windows / macOS / Android / iOS)"],
            placeholder: "Example:\nChrome 131 on Windows 11\n\nAnything else helpful (optional):",
            sendText: "Send info",
          },
        }
      ); return;
    }
    try {
      console.log("User initiated connection...");

      const requestFtmsDevice = () =>
        navigator.bluetooth.requestDevice({
          // Prefer the controllable-trainer profile first (FTMS)
          filters: [{ services: [FTMS_SERVICE_UUID] }],
          optionalServices: [FTMS_SERVICE_UUID, CYCLING_POWER_SERVICE_UUID, CSCS_SERVICE_UUID],
        });

      const requestExpandedDevice = () =>
        navigator.bluetooth.requestDevice({
          // Expanded search: show FTMS OR sensors-only metrics profiles
          filters: [
            { services: [FTMS_SERVICE_UUID] },
            { services: [CYCLING_POWER_SERVICE_UUID] },
            { services: [CSCS_SERVICE_UUID] },
          ],
          optionalServices: [FTMS_SERVICE_UUID, CYCLING_POWER_SERVICE_UUID, CSCS_SERVICE_UUID],
        });

      const connectAndSetup = async (selectedDevice) => {
        const server = await selectedDevice.gatt.connect();
        console.log("Device connected");

        // No persistence: every connection starts under ERG control.
        setErgEnabled(true);
        ergEnabledRef.current = true;
        ergReleasedRef.current = false;

        //Only works with acceptAllDevices : true
        //const services = await server.getPrimaryServices();
        //console.log("Services disponibles :");
        //for (const service of services) {
        //    console.log(`Service UUID: ${service.uuid}`);
        //}
        //

        let serviceFTMSLocal = null; // Par défaut à null
        let serviceCSCSLocal = null;
        let serviceCPLocal = null;

        try {
          serviceFTMSLocal = await server.getPrimaryService(FTMS_SERVICE_UUID);
          console.log("serviceFTMS trouvé :", serviceFTMSLocal);
          setCanErgControl(true);
          // Important: update ref immediately; state updates are async and we might request control right after.
          canErgControlRef.current = true;

          // Does this trainer understand "simulate a flat road" (0x11)?
          // If not, releases fall back to Reset (0x01).
          try {
            const featureChar = await serviceFTMSLocal.getCharacteristic(
              FTMS_FEATURE_CHARACTERISTIC_UUID
            );
            const featureValue = await featureChar.readValue();
            canSimulateFlatRef.current = parseIndoorBikeSimulationSupported(featureValue);
            setCanSimulateFlat(canSimulateFlatRef.current);
          } catch (e) {
            console.log("FTMS: feature characteristic unreadable, release will use Reset", e);
            canSimulateFlatRef.current = false;
            setCanSimulateFlat(false);
          }
          console.log(
            `FTMS: indoor bike simulation supported = ${canSimulateFlatRef.current}`
          );
        } catch (error) {
          console.log("serviceFTMS non disponible, serviceFTMS = null");
          setCanErgControl(false);
          canErgControlRef.current = false;
          canSimulateFlatRef.current = false;
          setCanSimulateFlat(false);
        }

        try {
          // Needed for power and for some trainers' cadence
          serviceCPLocal = await server.getPrimaryService(CYCLING_POWER_SERVICE_UUID);
          setServiceCP(serviceCPLocal);
          console.log("serviceCP", serviceCPLocal);
        } catch (error) {
          console.log("serviceCP non disponible, serviceCP = null");
          setServiceCP(null);
        }

        if (useCadenceService === "CSCS")  // otherwise not needed at all
        {
          try {
            serviceCSCSLocal = await server.getPrimaryService(CSCS_SERVICE_UUID) // cadence
            setServiceCSCS(serviceCSCSLocal);
            console.log("serviceCSCS", serviceCSCSLocal);
          } catch (error) {
            console.log("serviceCSCS non disponible, serviceCSCS = null");
            setServiceCSCS(null);
            serviceCSCSLocal = null;
          }
        }

        setDeviceHT(selectedDevice);
        setServiceFTMS(serviceFTMSLocal);

        if (serviceFTMSLocal) {
          const controlPointUuid = FITNESS_MACHINE_CONTROL_POINT_UUID; // Fitness Machine Control Point
          try {
            const controlPoint = await serviceFTMSLocal.getCharacteristic(
              controlPointUuid
            );
            // Tell the safe controller about this connection / characteristic immediately.
            serviceFTMSRef.current = serviceFTMSLocal;
            ftmsControlPointRef.current = controlPoint;
            ftmsControlGrantedRef.current = false;
            ftmsStateRef.current.blocked = true;

            controlPoint.addEventListener(
              "characteristicvaluechanged",
              handleIndications
            );
            await controlPoint.startNotifications();
            // Request Control via the safe controller (rate-limited + visibility/paused safe).
            console.log("Sending Control request to", controlPoint);
            await ftmsRequestControl();
            if (ftmsControlGrantedRef.current) {
              console.log("Control request sent.");
            } else {
              console.warn("Control request did not complete (blocked or failed).");
            }
          } catch (error) {
            console.error("Failed to request control", error);
          }
        }
        if (!serviceCPLocal && !serviceCSCSLocal) {
          await alertDlg(
            "We connected to the device, but it doesn't expose Cycling Power (CP) or Speed/Cadence (CSCS).\n\nPlease pick a different device profile (often the one named like your trainer, not a generic sensor).",
            {
              title: "No power/cadence services found",
              reportBug: {
                alertName: "Bluetooth: Connected device has neither CP nor CSCS",
                fields: ["Trainer brand and model", "What device name did you select?"],
                placeholder:
                  "Example:\n- Trainer: ...\n- Selected device name: ...\n\nAnything else helpful (optional):",
                sendText: "Send info",
              },
            }
          );
          throw new Error("No CP/CSCS services available on selected device");
        }

        if (serviceCPLocal) {
          console.log("Subscribing to service CP")
          await subscribeToMetricCP(serviceCPLocal, useCadenceService === "CP");
        }
        if (serviceCSCSLocal) {
          console.log("Subscribing to service CSCS")
          await subscribeToMetricCSCS(serviceCSCSLocal);
        }
        setIsConnected(true);
        console.log("setting set is connected te true");
        onConnect(selectedDevice);

        return { server, serviceFTMSLocal };
      };

      const showSensorsOnlyInfo = async ({ title }) => {
        await alertDlg(
          "Connected, but ERG control (FTMS) was not available.\n\n" +
            "We'll continue in sensors-only mode (power/cadence). You can still train here, but you must adjust resistance manually on the trainer.\n\n" +
            "Tips:\n" +
            "- Use a third-party app to control ERG.\n" +
            "- Make sure your trainer supports Bluetooth Low Energy (BLE). Classic Bluetooth / ANT+ won't work in a web browser.\n" +
            "- Make sure your trainer is NOT connected to another app/device (Zwift, TrainerRoad, phone app, head unit, etc.).\n\n" +
            "Please send the info below so we can improve compatibility.",
          {
            title: title || "ERG control not found (FTMS)",
            reportBug: {
              alertName: "Bluetooth: Connected without FTMS (sensors-only fallback)",
              fields: [
                "Trainer brand and model",
                "Browser + OS",
                "What device name did you select (and what other names were listed)?",
              ],
              placeholder:
                "Example:\n" +
                "- Trainer: Elite Suito\n" +
                "- Browser: Chrome 131\n" +
                "- OS: Windows 11\n" +
                "- Bluetooth list: \"Elite Suito\", \"Elite Suito Power\"...\n" +
                "- Selected: \"Elite Suito Power\"\n\n" +
                "Anything else helpful (optional):",
              sendText: "Send info",
            },
          }
        );
      };

      // Strategy:
      // - If user explicitly requested sensors-only: go straight to expanded search.
      // - Otherwise: FTMS-only first, then offer expanded search if ERG isn't found / user cancels.
      const doExpandedSearch = async () => {
        try {
          device = await requestExpandedDevice();
        } catch (e) {
          // Cancel / nothing selected in chooser
          if (e?.name === "NotFoundError") {
            await alertDlg(
              "Bluetooth device selection was cancelled.\n\n" +
              "If you want help connecting your trainer, please send the info below (especially what device names you saw).\n\n" +
              "Tips:\n" +
              "- Make sure your trainer supports Bluetooth Low Energy (BLE). Classic Bluetooth / ANT+ won't work in a web browser.\n" +
              "- Make sure your trainer is NOT connected to another app/device (Zwift, TrainerRoad, phone app, head unit, etc.).",
              {
                title: "Bluetooth selection cancelled",
                reportBug: {
                  alertName: "Bluetooth: Expanded search cancelled",
                  fields: [
                    "Trainer brand and model",
                    "Browser + OS",
                    "What device names did you see in the Bluetooth list?",
                    "Why did you cancel? (couldn't find it / too many options / other)",
                  ],
                  placeholder:
                    "Example:\n" +
                    "- Trainer: Elite Suito\n" +
                    "- Browser: Chrome 131\n" +
                    "- OS: Windows 11\n" +
                    "- Bluetooth list: \"Elite Suito\", \"Elite Suito Power\"...\n" +
                    "- Cancel reason: Couldn't find trainer / unsure which to pick\n\n" +
                    "Anything else helpful (optional):",
                  sendText: "Send info",
                },
              }
            );
            // Important: don't fall back to the FTMS troubleshooting modal after the user cancelled
            // the expanded search - we already showed the info dialog.
            const err = new Error("User cancelled expanded search");
            err.code = "EXPANDED_CANCELLED";
            throw err;
          }
          throw e;
        }

        const { serviceFTMSLocal } = await connectAndSetup(device);
        // If FTMS found: connect silently (user shouldn't suspect anything)
        if (!serviceFTMSLocal) {
          await showSensorsOnlyInfo({ title: "ERG not found - connected sensors-only" });
        }
      };

      if (requestedMode === "sensorsOnly") {
        await doExpandedSearch();
        return;
      }

      // FTMS-only attempt
      try {
        device = await requestFtmsDevice();
      } catch (e) {
        // Cancel / nothing selected => let UI show the "ERG not found? Expand search" modal
        if (e?.name === "NotFoundError") {
          const err = new Error("User cancelled FTMS connect");
          err.code = "FTMS_CANCELLED";
          throw err;
        }
        throw e;
      }

      const { serviceFTMSLocal } = await connectAndSetup(device);
      // If FTMS filter led to a connection without FTMS (weird but possible), continue sensors-only and ask for info.
      if (!serviceFTMSLocal) {
        await showSensorsOnlyInfo({ title: "ERG not found (FTMS)" });
      }

    } catch (error) {
      console.error("Failed to connect:", error);
      onDisconnect();
      setIsConnected(false);
      try {
        device?.gatt?.disconnect();
      } catch (e) {
        console.log("Failed to disconnect after error when connecting:", e);
      }
      throw error;
    } finally { }
  };

  const disconnectFromDevice = async (onDisconnect) => {
    try {
      if (!(await confirm("Disconnect home trainer ?"))) {
        return;
      }
      console.log("User initiated disconnection...");
      // Lifecycle safety: cancel timers + clear pending writes + block future writes until control is re-established.
      ftmsReset({ block: true });

      if (deviceHT) {
        if (serviceFTMS) {
          try {
            const controlPointUuid = FITNESS_MACHINE_CONTROL_POINT_UUID;
            const characteristic = await serviceFTMS.getCharacteristic(
              controlPointUuid
            );
            await characteristic.stopNotifications();
            characteristic.removeEventListener(
              "characteristicvaluechanged",
              handleIndications
            );
            console.log("Stopped notifications for FTMS service.");
          } catch (error) {
            console.error(
              "Failed to stop notifications for FTMS service:",
              error
            );
          }
        }

        if (serviceCP) {
          try {
            const characteristic = await serviceCP.getCharacteristic(CYCLING_POWER_MEASUREMENT_UUID);
            await characteristic.stopNotifications();
            characteristic.removeEventListener(
              "characteristicvaluechanged",
              handleCharacteristicValueChangedCP
            );
            console.log("Stopped notifications for CP service.");
            setCurrentCadence(null);
            setCurrentPower(null);
          } catch (error) {
            console.error(
              "Failed to stop notifications for CP service:",
              error
            );
          }
        }

        if (serviceCSCS) {
          try {
            const characteristic = await serviceCSCS.getCharacteristic(CSC_MEASUREMENT_UUID);
            await characteristic.stopNotifications();
            characteristic.removeEventListener(
              "characteristicvaluechanged",
              handleCharacteristicValueChangedCSCS
            );
            console.log("Stopped notifications for CSCS service.");
            setCurrentCadence(null);
            setCurrentPower(null);
          } catch (error) {
            console.error(
              "Failed to stop notifications for CSCS service:",
              error
            );
          }
        }

        await deviceHT.gatt.disconnect();
        console.log("Device disconnected.");
      } else {
        console.log("No device is connected.");
      }


      setDeviceHT(null);
      setServiceFTMS(null);
      setServiceCP(null);
      setServiceCSCS(null);
      setIsConnected(false);
      onDisconnect();
    } catch (error) {
      console.error("Failed to disconnect:", error);
    }
  };

  return {
    // Connection state
    isConnected,
    isBPMConnected,
    canErgControl,
    ergEnabled,
    canSimulateFlat,
    deviceHT,
    deviceBPM,

    // Bluetooth services
    serviceFTMS,
    serviceCP,
    serviceCSCS,
    serviceBPM,

    // Sensor data
    currentCadence,
    currentPower,
    currentBPM,
    lastUpdatePower,
    lastUpdateCad,

    // Debug data
    currentHTValueCP,
    currentHTValueCSCS,

    // Setters (needed by HomeTrainer for simulation + reset flows)
    setServiceFTMS,
    setServiceCP,
    setServiceCSCS,
    setServiceBPM,
    setIsConnected,
    setIsBPMConnected,
    setDeviceHT,
    setDeviceBPM,
    setCurrentCadence,
    setCurrentPower,
    setCurrentBPM,
    setLastUpdatePower,
    setLastUpdateCad,
    setCurrentHTValueCP,
    setCurrentHTValueCSCS,

    // Functions
    connectToDevice,
    disconnectFromDevice,
    setTargetPowerViaBt,
    setErgEnabled,
    releaseErg,
    engageErg,
    setSimulationGradient,
    connectToBPM,
    disconnectFromBPM,
  };
};
